import { useCallback } from 'react';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail/utils';
import { IRewardFragment } from 'generated/graphql-gateway';
import { OfferType } from 'generated/rbi-graphql';
import { useFeatureEarningCalculationQuery } from 'generated/sanity-graphql';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { mapEntryTypeToOfferType } from 'pages/cart/utils';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { IAddToCartSelectionAttributes } from 'state/cdp/types';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocationContext } from 'state/location';
import { ILocation } from 'state/location/types';
import { useLoyaltyContext } from 'state/loyalty';
import { useRewardsEvaluation } from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import {
  LoyaltyAppliedOffer,
  isDiscountReward,
  isLoyaltyOffer,
  isSurpriseOffer,
} from 'state/loyalty/types';
import { isAppliedOfferSwap } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { useServiceModeContext } from 'state/service-mode';
import { getCartEntryBenefit } from 'utils/cart/helper';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { goToMenu, routes } from 'utils/routing';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';
import { IMenuObjectToCartEntry, transformMenuObjectToCartItem } from 'utils/wizard';

type LocationStateType = ILocation['location']['state'] & {
  isFromCart?: boolean;
  isFromSurprise?: boolean;
};

export const useProductCart = () => {
  const { selectFavorite, tempFavorite: currentFavorite } = useFavoritesContext();
  const { location, navigate } = useLocationContext();
  const { getAvailableRewardFromCartEntry } = useLoyaltyContext();
  const { sanityRewardsMap } = useLoyaltyRewardsList();

  const { priceForItemOptionModifier, pricingFunction, priceForComboSlotSelection, isOffer } =
    useMenuContext();
  const cdp = useCdpContext();
  const { removeFromCart, upsertCart } = useOrderContext();
  const { isDelivery } = useServiceModeContext();
  const { selectedProduct, productQuantity, userSelections, manualModifications } =
    useProductWizardContext();

  const dispatch = useAppDispatch();
  const editingCartEntry = useEditingCartEntry();
  const { totalPrice } = useProductPrice();
  const { incentiveNotInMenu } = useRedeemReward();
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const rewardIdParam = useGetUrlSearchParam('rewardId');

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const selectedAppliedOffersAtCheckoutMap = useAppSelector(
    selectors.loyalty.selectAppliedOffersAtCheckoutMap
  );
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const { engineRewardsMap } = useRewardsEvaluation();

  const selectedOffer = selectedLoyaltyOffer;

  const { data: earningCalculationData } = useFeatureEarningCalculationQuery({
    variables: { id: 'earningCalculation' },
  });
  const standardOffersLimit =
    earningCalculationData?.EarningCalculation?.offerRedemptionLimits?.standardOffersLimit || 1;

  const { isFromCart, isFromSurprise } = (location.state as LocationStateType) || {};

  const handleRewardRedemption = useCallback(
    (cartEntries: ICartEntry[]) => {
      if (rewardRedemptionBenefitIdParam) {
        const entryRedemption = getCartEntryBenefit(
          cartEntries,
          getAvailableRewardFromCartEntry,
          rewardRedemptionBenefitIdParam
        );

        const sanityReward =
          rewardIdParam && sanityRewardsMap ? sanityRewardsMap[rewardIdParam] : null;
        const isRewardDiscount = isDiscountReward(sanityReward);

        const rewardInfo =
          rewardIdParam && sanityReward && isRewardDiscount
            ? availableLoyaltyRewardsMap[sanityReward?.incentives?.[0]?._id ?? '']
            : engineRewardsMap[sanityRewardsMap?.[rewardIdParam!].loyaltyEngineId!];

        cdp.trackEvent({
          name: CustomEventNames.REWARD_ADDED_TO_ORDER,
          type: EventTypes.Other,
          attributes: {
            engineId: rewardInfo?.id ?? '',
            sanityId: rewardInfo?.sanityId ?? '',
            name: rewardInfo?.name ?? '',
            redemptionMode: 'Online',
          },
        });

        if (entryRedemption) {
          dispatch(
            actions.loyalty.applyReward({
              rewardBenefitId: isRewardDiscount
                ? sanityReward?.incentives?.[0]?._id!
                : rewardRedemptionBenefitIdParam,
              cartId: entryRedemption.cartId,
              rewardObj: rewardInfo as IRewardFragment,
              // If a loyalty incentive is not available as a regular menu entry,
              // the quantity must match rewards applied
              ...(incentiveNotInMenu && { quantity: entryRedemption.quantity }),
              minimumSpend: rewardInfo?.minimumSpend,
              minimumTotalSpend: rewardInfo?.minimumTotalSpend,
            })
          );
        }
      }
    },
    [
      availableLoyaltyRewardsMap,
      dispatch,
      engineRewardsMap,
      getAvailableRewardFromCartEntry,
      incentiveNotInMenu,
      cdp,
      rewardIdParam,
      rewardRedemptionBenefitIdParam,
      sanityRewardsMap,
    ]
  );

  const handleOffers = useCallback(
    ({
      mainEntry,
      upsellEntries,
      modifiersInjected,
    }: IMenuObjectToCartEntry & { cartEntries: ICartEntry[] }) => {
      const offerPrice = totalPrice(modifiersInjected);
      if (selectedOffer && isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            // remove offer from cart if it is not a swap or stackable offer
            if (!offer.isStackable && !isAppliedOfferSwap(offer)) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }

        // If offer limit was reached remove first one
        if (appliedOffers.length >= standardOffersLimit) {
          removeFromCart({ cartId: appliedOffers[0].cartId });
        }

        const offerId = selectedOffer.loyaltyEngineId;

        const offerAppliedPreviouslyAtCheckout =
          offerId != null && selectedAppliedOffersAtCheckoutMap[offerId];

        const offerType = offerAppliedPreviouslyAtCheckout
          ? OfferType.PERSONALIZED
          : (selectedOffer as LoyaltyAppliedOffer).type;

        dispatch(
          actions.loyalty.applyOffer({
            id: offerId,
            type: offerType,
            cartId: mainEntry?.cartId,
            isStackable: selectedOffer.isStackable,
            isSurprise: isSurpriseOffer(selectedOffer),
            cmsId: selectedOffer._id,
            isAppliedAtCheckout: offerAppliedPreviouslyAtCheckout,
          })
        );

        if (!offerAppliedPreviouslyAtCheckout) {
          dispatch(actions.loyalty.setSelectedOffer(null));
        }

        upsertCart([
          {
            ...mainEntry,
            price: offerPrice,
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
        cdp.trackEvent({
          name: CustomEventNames.OFFER_ADDED_TO_ORDER,
          type: EventTypes.Other,
          attributes: {
            engineId: selectedOffer?.loyaltyEngineId ?? '',
            sanityId: selectedOffer?._id ?? '',
            name: getFirstStringInLocaleBlockContent(selectedOffer.name),
            redemptionMode: 'Online',
          },
        });
      }
    },
    [
      appliedOffers,
      dispatch,
      cdp,
      removeFromCart,
      selectedAppliedOffersAtCheckoutMap,
      selectedOffer,
      standardOffersLimit,
      totalPrice,
      upsertCart,
    ]
  );

  const addToOrder = useCallback(() => {
    const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
      selectedProduct,
      priceForItemOptionModifier,
      pricingFunction,
      priceForComboSlotSelection,
      {
        quantity: productQuantity,
        modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
        pickerSelections: userSelections.pickerAspects,
        comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        ),
        location,
        // quickConfigs, // ignore for now - only TH uses quickConfigs
        existingCartId: editingCartEntry?.cartId,
        shouldLogEvents: isOffer,
        cdp,
      },
      isDelivery
    );
    // Don't include the main entry if its an offer that gets set separately in offerCtx
    const cartEntries = isOffer ? upsellEntries : ([mainEntry, ...upsellEntries] as ICartEntry[]);

    // If we are currently editing a favorite  don't continue
    if (currentFavorite) {
      return selectFavorite({ cartEntries });
    }

    // Only update the cart if we actually have anything to add. (Offers will most likely not have anything here)
    if (cartEntries.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(cartEntries, selections);
    }

    handleRewardRedemption(cartEntries);

    if (isOffer && selectedOffer) {
      handleOffers({
        mainEntry,
        upsellEntries,
        modifiersInjected,
        cartEntries,
      });
    }

    hapticImpact({ style: HapticsImpactStyle.Heavy });

    // if editing and user coming from the cart or if is coming from the surprise modal
    if ((editingCartEntry && isFromCart) || isFromSurprise) {
      return navigate(routes.cart);
    }
    goToMenu({ navigate });
  }, [
    selectedProduct,
    priceForItemOptionModifier,
    pricingFunction,
    priceForComboSlotSelection,
    productQuantity,
    userSelections,
    location,
    editingCartEntry,
    isOffer,
    cdp,
    currentFavorite,
    handleRewardRedemption,
    selectedOffer,
    isFromCart,
    isFromSurprise,
    navigate,
    selectFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    handleOffers,
    isDelivery,
  ]);

  return {
    addToOrder,
  };
};
